import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import CancelDialogIcon from "@entrilia/shared/icons/CancelDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { UserAccess, UserAccessMatrixRow } from "./userAccessMatrixModel";
import {
  mapObjectInternalUserPermissionsToUserAccess,
  mapUserAccessToObjectInternalUserPermissionsRequest,
} from "./userAccessMatrixModel";

interface Props {
  objectId: string;
  objectType: string;
  removedRows: UserAccessMatrixRow[];
  currentAccessItems: UserAccess[];
  onClose: () => void;
  onSave: (updatedAccessItems: UserAccess[]) => void;
}

const updateObjectInternalUserPermissions = withErrorHandling(adminApi.updateObjectInternalUserPermissions);

const RemoveInternalUserAccessDialog = ({
  objectId,
  objectType,
  removedRows,
  currentAccessItems,
  onClose,
  onSave,
}: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    const payload = {
      userPermissions: currentAccessItems
        .filter((item) => !removedRows.some((r) => r.id === item.id))
        .map(mapUserAccessToObjectInternalUserPermissionsRequest),
    };

    setIsSaving(true);
    const [updatedUsers, error] = await updateObjectInternalUserPermissions(objectType, objectId, payload);
    setIsSaving(false);

    if (error) {
      logError(error, "[RemoveInternalUserAccessDialog] updateObjectInternalUserPermissions");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("User(s) removed successfully");
    onSave(updatedUsers.map(mapObjectInternalUserPermissionsToUserAccess));
    onClose();
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Remove</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <CancelDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography noWrap variant="h6">
              {removedRows.length === 1 ? "Remove user?" : "Remove users?"}
            </Typography>
            {removedRows.length === 1 && (
              <Typography color="textSecondary" sx={{ textAlign: "center" }}>
                You&apos;re about to remove <strong>{removedRows[0]?.name}</strong> from the portfolio company. Access
                to this company will be lost.
              </Typography>
            )}
            {removedRows.length > 1 && (
              <Typography color="textSecondary" sx={{ textAlign: "center" }}>
                You&apos;re about to remove <strong>{removedRows.length} users</strong> from the portfolio company.
                Access to this company will be lost.
              </Typography>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={isSaving} onClick={handleSave}>
          Remove
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default RemoveInternalUserAccessDialog;
